/**
 * Global Search (Spotlight/Raycast style)
 *
 * A command palette that can be opened from anywhere with ⌘K (Mac) or Ctrl+K (Windows/Linux).
 * Searches across all resources: documents, dashboards, trackers, enrichments, alerts.
 */

import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaChartBar,
  FaCrosshairs,
  FaMagic,
  FaBell,
  FaChevronRight,
  FaChevronDown,
  FaKeyboard,
  FaTimes,
  FaFileAlt,
} from "react-icons/fa";
import {
  useDashboardsStore,
  useTrackersStore,
  useEnrichmentsStore,
  useAlertsStore,
} from "@/hooks";
import { useDocumentsSearch } from "@/api";
import { Dashboard } from "@/models/dashboard";
import { Tracker } from "@/models/tracker";
import { Enrichment } from "@/models/enrichment";
import { Alert } from "@/models/alert";
import { Content } from "@/models/document";

// ============================================================================
// Types
// ============================================================================

type ResourceType = "dashboard" | "tracker" | "enrichment" | "alert" | "document";

type ResourceItem = Dashboard | Tracker | Enrichment | Alert | Content;

interface ResourceTypeConfig {
  type: ResourceType;
  label: string;
  labelPlural: string;
  icon: React.ReactNode;
  iconSmall: React.ReactNode;
  link: string;
  getItemLink: (id: string) => string;
  getName: (item: ResourceItem) => string;
  getDescription: (item: ResourceItem) => string;
  color: string;
  bgColor: string;
}

interface SearchResultItem {
  id: string;
  type: ResourceType;
  name: string;
  description: string;
  link: string;
}

// Navigation item type
type NavItem =
  | { kind: "header"; type: ResourceType }
  | { kind: "entity"; entity: SearchResultItem; globalIndex: number };

// ============================================================================
// Configuration
// ============================================================================

const resourceTypes: ResourceTypeConfig[] = [
  {
    type: "dashboard",
    label: "Dashboard",
    labelPlural: "Dashboards",
    icon: <FaChartBar size={14} />,
    iconSmall: <FaChartBar size={12} />,
    link: "/dashboards",
    getItemLink: (id) => `/dashboards?id=${id}`,
    getName: (item) => (item as Dashboard).name || "Sans nom",
    getDescription: (item) => (item as Dashboard).description || "",
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
  },
  {
    type: "tracker",
    label: "Tracker",
    labelPlural: "Trackers",
    icon: <FaCrosshairs size={14} />,
    iconSmall: <FaCrosshairs size={12} />,
    link: "/trackers",
    getItemLink: (id) => `/trackers?id=${id}`,
    getName: (item) => {
      const tracker = item as Tracker;
      return (
        tracker.name ||
        (tracker.metadata?.name as string) ||
        tracker.path ||
        "Sans nom"
      );
    },
    getDescription: (item) => {
      const tracker = item as Tracker;
      return tracker.renderer || "";
    },
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
  },
  {
    type: "enrichment",
    label: "Enrichment",
    labelPlural: "Enrichments",
    icon: <FaMagic size={14} />,
    iconSmall: <FaMagic size={12} />,
    link: "/enrichments",
    getItemLink: (id) => `/enrichments?id=${id}`,
    getName: (item) => {
      const enrichment = item as Enrichment;
      return (
        enrichment.name || (enrichment.metadata?.name as string) || "Sans nom"
      );
    },
    getDescription: (item) => {
      const enrichment = item as Enrichment;
      return enrichment.type || "";
    },
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
  },
  {
    type: "alert",
    label: "Alert",
    labelPlural: "Alerts",
    icon: <FaBell size={14} />,
    iconSmall: <FaBell size={12} />,
    link: "/alerts",
    getItemLink: (id) => `/alerts?id=${id}`,
    getName: (item) => {
      const alert = item as Alert;
      return alert.name || (alert.metadata?.name as string) || "Sans nom";
    },
    getDescription: (item) => {
      const alert = item as Alert;
      return alert.config?.type || alert.description || "";
    },
    color: "text-red-400",
    bgColor: "bg-red-500/10",
  },
  {
    type: "document",
    label: "Document",
    labelPlural: "Documents",
    icon: <FaFileAlt size={14} />,
    iconSmall: <FaFileAlt size={12} />,
    link: "/search",
    // This will be overridden per-document to use external URL when available
    getItemLink: (id) => `/search?documentId=${id}`,
    getName: (item) => {
      const doc = item as Content;
      return doc.normalized?.title || doc.normalized?.text?.substring(0, 80) || "Sans titre";
    },
    getDescription: (item) => {
      const doc = item as Content;
      const username = doc.normalized?.author?.username;
      const type = doc.normalized?.type;
      const parts = [type, username].filter(Boolean);
      return parts.join(" • ") || "";
    },
    color: "text-green-400",
    bgColor: "bg-green-500/10",
  },
];

// ============================================================================
// Context for global access
// ============================================================================

interface GlobalSearchContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null);

export const useGlobalSearch = () => {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error("useGlobalSearch must be used within GlobalSearchProvider");
  }
  return context;
};

// ============================================================================
// Global Search Modal Component
// ============================================================================

const GlobalSearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Track collapsed state for each type - all expanded by default
  const [collapsedTypes, setCollapsedTypes] = useState<Set<ResourceType>>(new Set());

  // Track highlighted nav item index
  const [highlightedNavIndex, setHighlightedNavIndex] = useState(0);

  // Debounce search query for documents API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all resources
  const { resources: dashboards = [], isLoading: isLoadingDashboards } =
    useDashboardsStore();
  const { resources: trackers = [], isLoading: isLoadingTrackers } =
    useTrackersStore();
  const { resources: enrichments = [], isLoading: isLoadingEnrichments } =
    useEnrichmentsStore();
  const { resources: alerts = [], isLoading: isLoadingAlerts } = useAlertsStore();

  // Search documents via API (only when query is at least 2 chars)
  const { data: documentsData, isLoading: isLoadingDocuments } = useDocumentsSearch(
    {
      query: debouncedQuery.length >= 2
        ? { query_string: { query: `*${debouncedQuery}*`, default_operator: "AND" } }
        : {},
      limit: 10,
    },
    {
      enabled: debouncedQuery.length >= 2,
      staleTime: 30000,
    }
  );
  const documents: Content[] = documentsData || [];

  const isLoading =
    isLoadingDashboards ||
    isLoadingTrackers ||
    isLoadingEnrichments ||
    isLoadingAlerts ||
    (debouncedQuery.length >= 2 && isLoadingDocuments);

  // Search results
  const searchResults = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const results: SearchResultItem[] = [];

    // If no query, show recent/all items (limited)
    const shouldShowAll = query.length === 0;
    const maxPerType = shouldShowAll ? 3 : 10;

    // Search dashboards
    const dashboardConfig = resourceTypes.find((r) => r.type === "dashboard")!;
    let dashboardCount = 0;
    dashboards.forEach((d) => {
      if (dashboardCount >= maxPerType) return;
      const name = dashboardConfig.getName(d);
      const description = dashboardConfig.getDescription(d);
      if (
        shouldShowAll ||
        name.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      ) {
        results.push({
          id: d.id,
          type: "dashboard",
          name,
          description,
          link: dashboardConfig.getItemLink(d.id),
        });
        dashboardCount++;
      }
    });

    // Search trackers
    const trackerConfig = resourceTypes.find((r) => r.type === "tracker")!;
    let trackerCount = 0;
    trackers.forEach((t) => {
      if (trackerCount >= maxPerType) return;
      const name = trackerConfig.getName(t);
      const description = trackerConfig.getDescription(t);
      if (
        shouldShowAll ||
        name.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      ) {
        results.push({
          id: t.id,
          type: "tracker",
          name,
          description,
          link: trackerConfig.getItemLink(t.id),
        });
        trackerCount++;
      }
    });

    // Search enrichments
    const enrichmentConfig = resourceTypes.find((r) => r.type === "enrichment")!;
    let enrichmentCount = 0;
    enrichments.forEach((e) => {
      if (enrichmentCount >= maxPerType) return;
      const name = enrichmentConfig.getName(e);
      const description = enrichmentConfig.getDescription(e);
      if (
        shouldShowAll ||
        name.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      ) {
        results.push({
          id: e.id,
          type: "enrichment",
          name,
          description,
          link: enrichmentConfig.getItemLink(e.id),
        });
        enrichmentCount++;
      }
    });

    // Search alerts
    const alertConfig = resourceTypes.find((r) => r.type === "alert")!;
    let alertCount = 0;
    alerts.forEach((a) => {
      if (alertCount >= maxPerType) return;
      const name = alertConfig.getName(a);
      const description = alertConfig.getDescription(a);
      if (
        shouldShowAll ||
        name.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      ) {
        results.push({
          id: a.id,
          type: "alert",
          name,
          description,
          link: alertConfig.getItemLink(a.id),
        });
        alertCount++;
      }
    });

    // Search documents (already filtered by API, just add them)
    // Only show documents if there's a search query (no "show all" for documents)
    if (query.length >= 2) {
      const documentConfig = resourceTypes.find((r) => r.type === "document")!;
      documents.slice(0, maxPerType).forEach((doc) => {
        const name = documentConfig.getName(doc);
        const description = documentConfig.getDescription(doc);
        // Use external URL if available, otherwise use internal link
        const externalUrl = doc.normalized?.url;
        results.push({
          id: doc.id,
          type: "document",
          name,
          description,
          link: externalUrl || documentConfig.getItemLink(doc.id),
        });
      });
    }

    return results;
  }, [searchQuery, dashboards, trackers, enrichments, alerts, documents]);

  // Group results by type
  const groupedResults = useMemo(() => {
    const groups: Record<ResourceType, SearchResultItem[]> = {
      dashboard: [],
      tracker: [],
      enrichment: [],
      alert: [],
      document: [],
    };
    searchResults.forEach((result) => {
      groups[result.type].push(result);
    });
    return groups;
  }, [searchResults]);

  // Type order for display - documents shown first when searching
  const typeOrder: ResourceType[] = searchQuery.length >= 2 
    ? ["document", "tracker", "dashboard", "enrichment", "alert"]
    : ["tracker", "dashboard", "enrichment", "alert"];

  // Sorted grouped results
  const sortedGroupedResults = typeOrder
    .filter((type) => groupedResults[type]?.length > 0)
    .map((type) => [type, groupedResults[type]] as [ResourceType, SearchResultItem[]]);

  // Build navigation items
  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];
    let globalIdx = 0;
    sortedGroupedResults.forEach(([type, typeResults]) => {
      items.push({ kind: "header", type });
      if (!collapsedTypes.has(type)) {
        typeResults.forEach((result) => {
          items.push({ kind: "entity", entity: result, globalIndex: globalIdx });
          globalIdx++;
        });
      } else {
        globalIdx += typeResults.length;
      }
    });
    return items;
  }, [sortedGroupedResults, collapsedTypes]);

  const currentNavItem = navItems[highlightedNavIndex];

  // Toggle collapse
  const toggleTypeCollapse = (type: ResourceType) => {
    setCollapsedTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const expandType = (type: ResourceType) => {
    setCollapsedTypes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(type);
      return newSet;
    });
  };

  const collapseType = (type: ResourceType) => {
    setCollapsedTypes((prev) => {
      const newSet = new Set(prev);
      newSet.add(type);
      return newSet;
    });
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setHighlightedNavIndex(0);
      setCollapsedTypes(new Set());
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    const item = navItems[highlightedNavIndex];
    if (item) {
      const key =
        item.kind === "header" ? `header-${item.type}` : `entity-${item.globalIndex}`;
      const el = itemRefs.current.get(key);
      if (el && listRef.current) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedNavIndex, navItems]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedNavIndex((prev) => Math.min(prev + 1, navItems.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedNavIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "ArrowRight":
          e.preventDefault();
          if (currentNavItem?.kind === "header") {
            expandType(currentNavItem.type);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (currentNavItem?.kind === "header") {
            collapseType(currentNavItem.type);
          } else if (currentNavItem?.kind === "entity") {
            const entityType = currentNavItem.entity.type;
            collapseType(entityType);
            const headerIndex = navItems.findIndex(
              (item) => item.kind === "header" && item.type === entityType
            );
            if (headerIndex >= 0) {
              setHighlightedNavIndex(headerIndex);
            }
          }
          break;
        case "Enter":
          e.preventDefault();
          if (currentNavItem?.kind === "header") {
            toggleTypeCollapse(currentNavItem.type);
          } else if (currentNavItem?.kind === "entity") {
            // For documents with external URLs, open in new tab
            const entity = currentNavItem.entity;
            if (entity.type === "document" && entity.link.startsWith("http")) {
              window.open(entity.link, "_blank", "noopener,noreferrer");
            } else {
              navigate(entity.link);
            }
            onClose();
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    },
    [navItems, currentNavItem, navigate, onClose]
  );

  // Handle result selection
  const handleSelectResult = useCallback(
    (result: SearchResultItem) => {
      // For documents with external URLs, open in new tab
      if (result.type === "document" && result.link.startsWith("http")) {
        window.open(result.link, "_blank", "noopener,noreferrer");
      } else {
        navigate(result.link);
      }
      onClose();
    },
    [navigate, onClose]
  );

  const getConfig = (type: ResourceType) =>
    resourceTypes.find((r) => r.type === type)!;

  if (!isOpen) return null;

  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[99999] flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="relative w-full max-w-2xl mx-4 bg-surface-elevated rounded-2xl shadow-2xl border border-arc-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-arc-border">
          <FaSearch className="text-content-tertiary flex-shrink-0" size={18} />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setHighlightedNavIndex(0);
            }}
            placeholder="Search documents, dashboards, trackers, alerts..."
            className="flex-1 bg-transparent text-lg text-content-primary placeholder:text-content-tertiary outline-none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-surface-secondary text-content-tertiary hover:text-content-primary transition-colors"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {/* Loading state */}
          {isLoading && (
            <div className="px-4 py-8 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-content-secondary">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                Loading resources...
              </div>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && searchResults.length === 0 && (
            <div className="px-4 py-12 text-center">
              <FaSearch className="text-content-tertiary mx-auto mb-3" size={32} />
              <p className="text-content-secondary">
                {searchQuery
                  ? `No results for "${searchQuery}"`
                  : "No resources found"}
              </p>
            </div>
          )}

          {/* Results list */}
          {!isLoading && searchResults.length > 0 && (
            <div className="py-2">
              {sortedGroupedResults.map(([type, typeResults]) => {
                const config = getConfig(type);
                const isCollapsed = collapsedTypes.has(type);
                const headerNavIndex = navItems.findIndex(
                  (item) => item.kind === "header" && item.type === type
                );
                const isHeaderHighlighted = highlightedNavIndex === headerNavIndex;

                return (
                  <div key={type} className="mb-1">
                    {/* Type header */}
                    <div
                      ref={(el) => {
                        if (el) itemRefs.current.set(`header-${type}`, el);
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleTypeCollapse(type)}
                      onMouseEnter={() => setHighlightedNavIndex(headerNavIndex)}
                      className={`
                        w-full px-4 py-2 flex items-center gap-3 transition-colors cursor-pointer
                        ${
                          isHeaderHighlighted
                            ? "bg-accent/20"
                            : "hover:bg-surface-secondary"
                        }
                      `}
                    >
                      <div
                        className={`transition-transform duration-150 ${
                          isHeaderHighlighted ? "text-accent" : "text-content-tertiary"
                        }`}
                      >
                        {isCollapsed ? (
                          <FaChevronRight className="h-3 w-3" />
                        ) : (
                          <FaChevronDown className="h-3 w-3" />
                        )}
                      </div>
                      <div className={`${config.bgColor} ${config.color} p-1.5 rounded-lg`}>
                        {config.icon}
                      </div>
                      <span
                        className={`text-xs font-semibold uppercase tracking-wider flex-1 text-left ${
                          isHeaderHighlighted ? "text-accent" : "text-content-tertiary"
                        }`}
                      >
                        {config.labelPlural}
                      </span>
                      <span
                        className={`text-xs tabular-nums px-2 py-0.5 rounded-full ${
                          isHeaderHighlighted
                            ? "bg-accent/20 text-accent"
                            : "bg-surface-tertiary text-content-tertiary"
                        }`}
                      >
                        {typeResults.length}
                      </span>
                    </div>

                    {/* Entities */}
                    {!isCollapsed && (
                      <div>
                        {typeResults.map((result) => {
                          const globalIndex = searchResults.findIndex(
                            (r) => r.id === result.id && r.type === result.type
                          );
                          const entityNavIndex = navItems.findIndex(
                            (item) =>
                              item.kind === "entity" && item.entity.id === result.id
                          );
                          const isEntityHighlighted =
                            highlightedNavIndex === entityNavIndex;

                          return (
                            <div
                              key={`${result.type}-${result.id}`}
                              ref={(el) => {
                                if (el)
                                  itemRefs.current.set(`entity-${globalIndex}`, el);
                              }}
                              className={`
                                flex items-center gap-3 pl-12 pr-4 py-2.5 cursor-pointer transition-colors
                                ${
                                  isEntityHighlighted
                                    ? "bg-accent/20"
                                    : "hover:bg-surface-secondary"
                                }
                              `}
                              onClick={() => handleSelectResult(result)}
                              onMouseEnter={() =>
                                setHighlightedNavIndex(entityNavIndex)
                              }
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-arc-border flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`text-sm font-medium truncate ${
                                    isEntityHighlighted
                                      ? "text-accent"
                                      : "text-content-primary"
                                  }`}
                                >
                                  {result.name}
                                </div>
                                {result.description && (
                                  <div className="text-xs text-content-tertiary truncate">
                                    {result.description}
                                  </div>
                                )}
                              </div>
                              {isEntityHighlighted && (
                                <span className="text-xs text-content-tertiary">
                                  ↵ to open
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-arc-border bg-surface-secondary flex items-center justify-between text-xs text-content-tertiary">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-surface-tertiary text-content-secondary font-mono">
                ↑↓
              </kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-surface-tertiary text-content-secondary font-mono">
                ↵
              </kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-surface-tertiary text-content-secondary font-mono">
                ←→
              </kbd>
              expand/collapse
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-surface-tertiary text-content-secondary font-mono">
              esc
            </kbd>
            close
          </span>
        </div>
      </motion.div>
    </motion.div>
  );

  return createPortal(content, document.body);
};

// ============================================================================
// Provider Component
// ============================================================================

export const GlobalSearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Global keyboard shortcut ⌘K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);

  return (
    <GlobalSearchContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
      <AnimatePresence>
        {isOpen && <GlobalSearchModal isOpen={isOpen} onClose={close} />}
      </AnimatePresence>
    </GlobalSearchContext.Provider>
  );
};

export default GlobalSearchProvider;
